import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["mobileFilterTypes", "itemsCount", "showSort", "open", "onCloseClick"];
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { useACNavigate } from 'academy-common-lib/hooks/useACNavigate';
import { parse, stringify } from 'hub-http/helpers/params';
import { Set as ImmutableSet } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import styled from 'foundations-theming/utils';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { FILTER_TYPES } from 'academy-common-lib/constants/TopFilterConstants';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParamInput } from 'academy-common-lib/hooks/searchParamHooks';
import { PanelCheckbox } from '../../components/Modals/FullScreenModal/PanelCheckbox';
import FullScreenModal from 'academy-common-lib/components/Modals/FullScreenModal/FullScreenModal';
import { getMyPlaylistsUrl } from '../../features/myPlaylists/myPlaylistsRouting';
import { isNoResetOptionFilter, isSearchFilter, isSingleOptionFilter } from 'academy-common-lib/features/filters/filtersUtils';
import { useDefaultFilterValue, useFilterOptions } from 'academy-common-lib/features/filters/filtersHooks';
import { useShowContentSourceFilter } from 'academy-common-lib/features/filters/components/DropdownContentSourceFilter';
import { useIsPartnerSearchPage } from '../../features/search/searchHooks';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const RotatingUIIcon = styled.span.withConfig({
  displayName: "MobileTopFilterPanel__RotatingUIIcon"
})(["transition:transform 200ms ease-out;transform:", ";"], ({
  $isOpen
}) => $isOpen ? 'rotate(-180deg)' : 'rotate(0deg)');
const ClearAllText = styled.div.withConfig({
  displayName: "MobileTopFilterPanel__ClearAllText"
})(["color:", ";font-size:", ";font-weight:", ";"], ({
  theme
}) => theme.color['text-inverse-default'], ({
  theme
}) => theme.typography['heading-50'].fontSize, ({
  theme
}) => theme.typography['heading-50'].fontWeight);
const AccordionSection = ({
  name,
  'data-test-id': dataTestId,
  children
}) => {
  const [open, setOpen] = useState(false);
  const handleToggleOpen = useCallback(() => {
    setOpen(b => !b);
  }, []);
  return /*#__PURE__*/_jsxs(View, {
    marginInlineStart: "500",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      borderBlockEndColor: "border-core-subtle",
      borderBlockEndStyle: "solid",
      borderBlockEndWidth: "100",
      "data-test-id": dataTestId,
      justifyContent: "space-between",
      onClick: handleToggleOpen,
      paddingBlock: "300",
      paddingInlineEnd: "500",
      children: [/*#__PURE__*/_jsx("strong", {
        children: name
      }), /*#__PURE__*/_jsx(RotatingUIIcon, {
        $isOpen: open,
        children: /*#__PURE__*/_jsx(DownIcon, {
          color: "fill-accent-blue-default"
        })
      })]
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      duration: 200,
      open: open,
      children: /*#__PURE__*/_jsx("div", {
        children: children
      })
    })]
  });
};
export const FilterGroup = ({
  filterType
}) => {
  const options = useFilterOptions(filterType);
  const [paramValue, changeHandler] = useSearchParamInput(filterType, false);
  const [__, changeHandlerReplace] = useSearchParamInput(filterType, true);
  const value = useDefaultFilterValue(paramValue, filterType);
  const valuesSet = ImmutableSet(value);
  const navigate = useACNavigate();
  const onChange = useCallback((item, e) => {
    if (filterType === 'myPlaylistsType') {
      navigate(getMyPlaylistsUrl({
        tab: item
      }));
    } else if (isSingleOptionFilter(filterType)) {
      changeHandlerReplace(e);
    } else {
      changeHandler(e);
    }
  }, [changeHandler, changeHandlerReplace, filterType, navigate]);
  return /*#__PURE__*/_jsxs(AccordionSection, {
    "data-test-id": `filter-type-${filterType}`,
    name: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `learningCenter.topFilters.${filterType}.filterTitle`
    }),
    children: [!isNoResetOptionFilter(filterType) && /*#__PURE__*/_jsx(PanelCheckbox, {
      checked: valuesSet.isEmpty(),
      "data-test-id": `${filterType}-filterReset`,
      onChange: changeHandlerReplace,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.topFilters.${filterType}.filterReset`
      })
    }, "filterReset"), options.map(item => /*#__PURE__*/_jsx(PanelCheckbox, {
      checked: valuesSet.contains(item),
      "data-test-id": `${filterType}-${item}`,
      onChange: e => onChange(item, e),
      value: item,
      children: filterType === 'language' ? LanguageNames[item.toLowerCase()] : /*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.topFilters.${filterType}.${item}`
      })
    }, item))]
  });
};
const filterTypes = Object.values(FILTER_TYPES);
const MobileTopFilterPanel = _ref => {
  let {
      mobileFilterTypes,
      itemsCount,
      showSort,
      open,
      onCloseClick
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const location = useLocation();
  const navigate = useACNavigate();
  const showContentSourceFilter = useShowContentSourceFilter();
  const isPartnerSearchPage = useIsPartnerSearchPage();
  const visibleMobileFilterTypes = mobileFilterTypes.filter(filterType => filterType !== FILTER_TYPES.CONTENT_SOURCE || showContentSourceFilter && !isPartnerSearchPage);
  const originalSearch = useRef(location.search);
  useEffect(() => {
    if (open) {
      originalSearch.current = location.search;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const handleCloseClick = useCallback(() => {
    navigate(Object.assign({}, location, {
      search: originalSearch.current
    }), {
      replace: true
    });
    onCloseClick();
  }, [navigate, location, onCloseClick]);
  const clearAllowedFilters = useCallback(() => {
    const params = parse(location.search.slice(1));
    Object.keys(params).forEach(filterType => {
      if (filterTypes.includes(filterType)) {
        delete params[filterType];
      }
    });
    navigate(`${location.pathname}?${stringify(params)}`, {
      replace: true
    });
  }, [navigate, location.pathname, location.search]);
  const applyDisabled = originalSearch.current === location.search;
  const clearDisabled = !location.search;
  const header = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      children: /*#__PURE__*/_jsxs(PanelHeader, {
        position: "relative",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          inlineSize: "100%",
          insetInlineEnd: "400",
          justifyContent: "end",
          position: "absolute",
          children: [/*#__PURE__*/_jsx(Button, {
            "data-test-id": "clear-filters",
            disabled: clearDisabled,
            onClick: clearAllowedFilters,
            paddingBlock: "200",
            paddingInline: "300",
            responsive: false,
            use: "unstyled",
            children: /*#__PURE__*/_jsx(ClearAllText, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.mobileFilters.clearAll"
              })
            })
          }), /*#__PURE__*/_jsx(Button, {
            "data-test-id": "apply-filters",
            disabled: applyDisabled,
            onClick: onCloseClick,
            responsive: false,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.mobileFilters.apply"
            })
          })]
        }), /*#__PURE__*/_jsx(View, {
          insetInlineStart: "500",
          position: "absolute",
          children: /*#__PURE__*/_jsx(CloseButton, {
            "data-test-id": "full-screen-modal-close",
            onClick: handleCloseClick,
            position: "static"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-surface-raised",
      borderBlockEndStyle: "solid",
      borderBlockEndWidth: "100",
      borderColor: "border-container-default",
      paddingBlock: "300",
      paddingInline: "500",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.mobileFilters.filterBy"
            })
          })
        }), /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.mobileFilters.numResults",
            options: {
              numResults: itemsCount
            }
          })
        })]
      })
    })]
  });
  return /*#__PURE__*/_jsxs(FullScreenModal, Object.assign({
    header: header,
    open: open
  }, restProps, {
    children: [filterTypes && visibleMobileFilterTypes.map(value => !isSearchFilter(value) && /*#__PURE__*/_jsx(FilterGroup, {
      filterType: value
    }, value)), showSort && /*#__PURE__*/_jsx(FilterGroup, {
      filterType: 'sortBy'
    })]
  }));
};
export default MobileTopFilterPanel;